import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "../context/Internationalization";
import { hasFirebaseAction } from "../auth/consoleSession";
import useDeploymentUpdate from "../Hooks/useDeploymentUpdate";
import { LOGIN_ROUTE, REGISTRATION_ROUTE } from "../routes";
import { toast } from "sonner";
import { Toaster } from "../shadcn/components/ui/sonner";

const TOASTER_ID = "deployment-update";
const TOAST_ID = "deployment-update-toast";

export default function DeploymentUpdateNotice() {
  const auth = useAuthContext();
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const currentPath = pathname.toLowerCase();
  const isAuthRoute = [LOGIN_ROUTE, REGISTRATION_ROUTE].some(
    (route) => currentPath === route || currentPath.startsWith(`${route}/`)
  );
  const enabled =
    auth.sessionStatus === "ready" &&
    auth.session?.kind === "established" &&
    !auth.inlineSignInPending &&
    !isAuthRoute &&
    !hasFirebaseAction(search);
  const updateAvailable = useDeploymentUpdate(enabled);

  const title = t("deployment_update_title");
  const description = t("deployment_update_description");
  const refreshLabel = t("deployment_update_refresh");
  const visible = enabled && updateAvailable;

  // A persistent Sonner toast on its own toaster (bottom centre). It has no
  // close button: the only action is to refresh. It hides immediately on
  // logout or navigation into an account/registration flow.
  useEffect(() => {
    // Only dismiss a toast that was shown: dismissing an unknown id would
    // block the next toast with that id.
    if (!visible) return undefined;
    toast.info(title, {
      id: TOAST_ID,
      toasterId: TOASTER_ID,
      description,
      duration: Infinity,
      dismissible: false,
      action: { label: refreshLabel, onClick: () => window.location.reload() },
    });
    return () => toast.dismiss(TOAST_ID);
  }, [visible, title, description, refreshLabel]);

  return (
    <Toaster
      id={TOASTER_ID}
      position="bottom-center"
      containerAriaLabel={title}
    />
  );
}
